/* eslint-disable max-lines */
import { DialogWithForm } from '@strata/react-dialog';
import { DynamicInputFormik } from '@strata/react-dynamic-input';
import {
    isPerformanceValueCaseAutomatic,
    isPerformanceValueCaseManual,
} from '@strata/react-performance';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik, FormikErrors, getIn, setIn } from 'formik';
import { ComponentProps, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { PerformanceRequirement } from 'api/performanceAPI';
import { FactDataTypes, PerformanceType, PerformanceTypes } from 'api/userSupportAPI';
import {
    ConversationContext,
    ConversationContexts,
    isFactValid,
    TopicFact,
} from 'services/userSupportHelpers';
import {
    ConversationTopic,
    useGetConversationTopicsQuery,
    useLazyGetTopicFactsQuery,
} from 'store/api/userSupportApi';

const contextToValues = {
    [ConversationContexts.CLAIM_REJECTION]: {
        topicShortTitle: 'Referral Issue',
        factValues: {
            'facts.referral_issue_cause.datum_value': ConversationContexts.CLAIM_REJECTION,
            'facts.additional_info__5.datum_value': 'Place of Service',
        },
    },
    [ConversationContexts.NEEDS_BY_DATE]: {
        topicShortTitle: 'Referral Issue',
        factValues: {
            'facts.referral_issue_cause.datum_value': ConversationContexts.NEEDS_BY_DATE,
        },
    },
    [ConversationContexts.PATIENT_DISCONTINUED_THERAPY]: {
        topicShortTitle: 'Referral Issue',
        factValues: {
            'facts.referral_issue_cause.datum_value':
                ConversationContexts.PATIENT_DISCONTINUED_THERAPY,
        },
    },
};

interface NewConversationFormSchema {
    topic: ConversationTopic;
    facts: Record<string, TopicFact>;
    details: {
        [FactDataTypes.PERFORMANCE_REF]?: Record<string, { name: string; type: PerformanceType }>;
    };
}

const initialFormik = {
    values: {},
    errors: {
        topic: validationStrings.requiredField.errorMessage,
    },
};

interface NewConversationDialogProps {
    onCreate: ({ topic, facts, details }: NewConversationFormSchema) => Promise<void>;
    onCancel: () => void;
    performanceEntries?: PerformanceRequirement[];
    openWithContext?: ConversationContext | boolean;
}

export const NewConversationDialog = ({
    onCreate,
    onCancel,
    performanceEntries = undefined,
    openWithContext = false,
}: NewConversationDialogProps) => {
    const { data: topics } = useGetConversationTopicsQuery(
        { anyPerformance: (performanceEntries ?? []).length > 0 },
        { skip: !openWithContext },
    );
    const [getTopicFacts] = useLazyGetTopicFactsQuery();
    const [filledFacts, setFilledFacts] = useState<{ key: string; ancestor?: string }[]>([]);
    const [initialFormikState, setInitialFormikState] = useState<{
        values: Partial<NewConversationFormSchema>;
        errors?: { topic: string };
    }>(initialFormik);

    const details: NewConversationFormSchema['details'] = {
        [FactDataTypes.PERFORMANCE_REF]: (performanceEntries ?? []).reduce((prev, curr) => {
            const anyManualTasks = isPerformanceValueCaseManual(curr.tasks);
            const anyAutomaticTasks = isPerformanceValueCaseAutomatic(curr.tasks);
            let type;
            if (anyManualTasks && anyAutomaticTasks) {
                type = PerformanceTypes.HYBRID;
            } else if (anyManualTasks) {
                type = PerformanceTypes.MANUAL;
            } else if (anyAutomaticTasks) {
                type = PerformanceTypes.AUTOMATIC;
            }
            return {
                ...prev,
                [curr.performance_requirement_id]: {
                    name: curr.value_case_name,
                    type,
                },
            };
        }, {}),
    };

    useEffect(() => {
        (async () => {
            if (typeof openWithContext === 'string' && contextToValues[openWithContext] && topics) {
                const { topicShortTitle, factValues } = contextToValues[openWithContext];
                const topic = topics.find((option) => option.shortTitle === topicShortTitle);
                if (topic) {
                    const topicFacts = await getTopicFacts({ topicId: topic.value }).unwrap();
                    if (topicFacts) {
                        let values = {
                            topic,
                            facts: topicFacts,
                        };
                        Object.entries(factValues).forEach(([key, value]) => {
                            values = setIn(values, key, value) as NewConversationFormSchema;
                        });
                        setInitialFormikState({
                            values,
                            errors: undefined,
                        });
                    }
                }
            }
        })();
        return () => setInitialFormikState(initialFormik);
    }, [openWithContext, topics, getTopicFacts]);

    return (
        <Formik
            initialValues={
                {
                    details,
                    ...initialFormikState.values,
                } as NewConversationFormSchema
            }
            initialErrors={initialFormikState.errors as FormikErrors<NewConversationFormSchema>}
            onSubmit={async (values, { resetForm }) => {
                await onCreate(values);
                resetForm();
            }}
            validationSchema={Yup.object({
                topic: Yup.object({}).required(validationStrings.requiredField.errorMessage),
                facts: Yup.lazy((facts: Record<string, TopicFact>) =>
                    facts
                        ? Yup.object({
                              ...Object.keys(facts).reduce((prev, curr) => {
                                  const obj = facts?.[curr];
                                  if (!isFactValid(obj, facts, details)) {
                                      return prev;
                                  }

                                  const objYupType = DynamicInputFormik.getYupType({
                                      fieldType: obj?.shape?.type,
                                      isRequired: obj?.is_required,
                                      customComponents: {},
                                  });

                                  return {
                                      ...prev,
                                      [curr]: Yup.object({
                                          datum_value:
                                              obj?.is_iterable === true
                                                  ? Yup.array(objYupType)
                                                  : objYupType,
                                      }),
                                  };
                              }, {}),
                          })
                        : Yup.mixed().notRequired(),
                ),
            })}
            enableReinitialize
        >
            {(formik) => {
                const handleFactEdit = async (editedFact: { key: string; ancestor?: string }) => {
                    const { key, ancestor } = editedFact;
                    const filledFactsSet = new Set(filledFacts.map((fact) => fact.key));
                    if (filledFactsSet.has(key)) {
                        const keyIndex = filledFacts.findIndex((fact) => fact.key === key);
                        const factsToClear = filledFacts
                            // Get all facts entered after this one
                            .slice(keyIndex + 1)
                            // If sibling (same ancestor), ignore
                            .filter((fact) => fact.ancestor !== ancestor)
                            .map((fact) => fact.key);
                        // Reset facts to clear
                        await Promise.all(
                            factsToClear.map((factKey) =>
                                formik.setFieldValue(`facts.${factKey}.datum_value`, undefined),
                            ),
                        );
                        await formik.setFieldTouched('facts', undefined, false);
                        formik.setFieldError('facts', undefined);
                        const factsToClearSet = new Set(factsToClear);
                        const factsToKeep = filledFacts.filter(
                            (fact) => !factsToClearSet.has(fact.key),
                        );
                        setFilledFacts(factsToKeep);
                    } else {
                        const filledFactsCopy = filledFacts;
                        filledFactsCopy.push(editedFact);
                        setFilledFacts(filledFactsCopy);
                    }
                };
                return (
                    <DialogWithForm
                        isOpen={!!openWithContext}
                        dialogTitle="Create Support Conversation"
                        isPrimaryButtonDisabled={!formik.isValid}
                        onSecondaryButtonClick={() => {
                            onCancel();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Create Conversation"
                        secondaryButtonLabel="Cancel"
                        dialogContent={
                            <>
                                <SuggestSingleSelectFormik
                                    id="topic"
                                    label="What can we help you with?"
                                    placeholderText="Select..."
                                    required
                                    values={topics}
                                    onChange={(newValue) => {
                                        (async () => {
                                            await formik.setFieldValue('topic', newValue);
                                            if (newValue) {
                                                const topicFacts = await getTopicFacts({
                                                    topicId: newValue.value,
                                                }).unwrap();
                                                if (topicFacts) {
                                                    await formik.setFieldValue('facts', topicFacts);
                                                }
                                            }
                                        })();
                                    }}
                                    verticalSpacingSmall={!!getIn(formik.values, 'facts')}
                                />
                                {Object.entries(formik.values?.facts ?? {})
                                    .sort(
                                        ([, a], [, b]) =>
                                            (a?.order_index ?? 0) - (b?.order_index ?? 0),
                                    )
                                    .map(([key, fact]) => {
                                        let component: ComponentProps<
                                            typeof DynamicInputFormik
                                        >['component'] = {
                                            ...fact?.shape,
                                            required: fact?.is_required,
                                            label: fact?.label,
                                            optionsDict: fact?.shape?.options_dict,
                                            showOther: fact?.shape?.show_other,
                                        };
                                        if (fact?.data_type === FactDataTypes.PERFORMANCE_REF) {
                                            component = {
                                                ...component,
                                                optionsDict: (performanceEntries ?? []).reduce(
                                                    (prev, curr) => ({
                                                        ...prev,
                                                        [curr.performance_requirement_id]:
                                                            curr.value_case_name,
                                                    }),
                                                    {},
                                                ),
                                            };
                                        }
                                        if (fact?.is_iterable) {
                                            component = {
                                                type: DynamicInputFormik.defaultFieldTypes
                                                    .FIELD_LIST,
                                                label: '',
                                                addItemLabel: fact?.add_item_label,
                                                itemComponent: component,
                                            };
                                        }
                                        if (
                                            !isFactValid(
                                                fact,
                                                formik.values?.facts,
                                                formik.values?.details,
                                            )
                                        ) {
                                            return null;
                                        }
                                        return (
                                            <DynamicInputFormik
                                                key={key}
                                                component={component}
                                                id={`facts.${key}.datum_value`}
                                                onChangeCallback={() => {
                                                    (() =>
                                                        handleFactEdit({
                                                            key,
                                                            ancestor: fact?.ancestor_name,
                                                        }))();
                                                }}
                                            />
                                        );
                                    })}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};
